import React, { useEffect, useState } from 'react';

import { updateOrCreateScript } from '../../../store/api/calls';
import Input from '../forms/Input';
import BaseModal from '../modals/BaseModal';

export interface ScriptEditTarget {
  id: number;
  name: string;
  is_active?: boolean;
}

interface ScriptEditFormProps {
  open: boolean;
  script: Partial<ScriptEditTarget>;
  onClose: () => void;
  onSaved?: () => void;
}

/** Modal form to edit a script's name and active flag. */
export default function ScriptEditForm({
  open,
  script,
  onClose,
  onSaved,
}: ScriptEditFormProps) {
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [nameError, setNameError] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(script.name || '');
    setIsActive(script.is_active !== false);
    setNameError(false);
    setSaving(false);
  }, [open, script.id, script.name, script.is_active]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!script.id || saving) return;

    const trimmed = name.trim();
    if (!trimmed) {
      setNameError(true);
      return;
    }

    setSaving(true);
    updateOrCreateScript({ id: script.id, name: trimmed, is_active: isActive })
      .then(() => {
        onClose();
        onSaved?.();
      })
      .finally(() => setSaving(false));
  };

  return (
    <BaseModal open={open} onClose={onClose} center>
      <form className="baseForm" onSubmit={handleSubmit} data-component="ScriptEditForm">
        <fieldset>
          <Input
            type="text"
            title="Name"
            dataKey="name"
            placeholder="Use something descriptive"
            value={name}
            onChange={(val: string) => {
              setName(val);
              setNameError(!val || !String(val).trim());
            }}
          />
          {nameError && <div className="input-error danger-text">Name is required.</div>}

          <Input
            type="switch"
            title="Active"
            checked={isActive}
            onChange={() => setIsActive((prev) => !prev)}
          />
        </fieldset>

        <div className="button-wrapper">
          <button
            type="submit"
            className={`button ${saving || nameError ? 'disabled' : ''}`}
            disabled={saving}
          >
            Submit
          </button>
          <button
            type="button"
            className="button button-outline"
            onClick={(ev) => {
              ev.preventDefault();
              onClose();
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </BaseModal>
  );
}
